// components/RefurbishedPhones/RefurbishedPhoneDetails.jsx
import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../components/Theme/ThemeProvider';
import Context from "../../context";
import addToCart from 'helpers/addToCart';
import { 
  ArrowLeft, 
  ShoppingCart, 
  Heart, 
  Shield, 
  CheckCircle, 
  Star, 
  Battery, 
  HardDrive, 
  Palette,
  Zap,
  Clock,
  Truck,
  RotateCcw,
  Smartphone
} from 'lucide-react';
import { summaryApi } from '../../common';
import { toast } from 'react-toastify';
import LikeButton from '../../components/ProductLike/LikeButton';

const RefurbishedPhoneDetails = () => {
  const { colors } = useTheme();
  const { phoneId } = useParams();
  const navigate = useNavigate();
  const { fetchUserAddToCart } = useContext(Context);
  
  const [phone, setPhone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);

  const fetchPhoneDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${summaryApi.getRefurbishedPhoneDetails.url}/${phoneId}`, {
        method: summaryApi.getRefurbishedPhoneDetails.method,
        
      });
      
      const data = await response.json();
      
      if (data.success) {
        setPhone(data.data);
      } else {
        toast.error('Phone not found');
        navigate('/refurbished');
      }
    } catch (error) {
      console.error('Error fetching phone details:', error);
      toast.error('Failed to load phone details');
      navigate('/refurbished');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (e) => {
    if (!phone || !phone.inStock) return;
    
    setAddingToCart(true);
    try {
      const productData = {
        productId: phone._id,
        storage: phone.storage || '',
        color: phone.color || '',
        ram: phone.ram || '',
        processor: phone.processor || '',
        screenSize: phone.screenSize || ''
      };
      
      await addToCart(e, productData, fetchUserAddToCart);
      toast.success('Added to cart successfully!');
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'Like New': return '#10B981';
      case 'Excellent': return '#059669';
      case 'Good': return '#D97706';
      case 'Fair': return '#DC2626';
      default: return colors.textSecondary;
    }
  };

  useEffect(() => {
    if (phoneId) {
      fetchPhoneDetails();
    }
  }, [phoneId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.bg }}>
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-transparent" style={{ borderTopColor: colors.primary }} />
      </div>
    );
  }

  if (!phone) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.bg }}>
        <div className="text-center">
          <Smartphone size={64} className="mx-auto mb-4 opacity-30" style={{ color: colors.textSecondary }} />
          <h2 className="text-2xl font-bold mb-2" style={{ color: colors.text }}>Phone not found</h2>
          <button
            onClick={() => navigate('/refurbished')}
            className="px-6 py-3 rounded-lg font-semibold text-white"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`
            }}
          >
            Back to Refurbished Phones
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bg }}>
      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/refurbished')}
        className="fixed top-6 left-6 z-50 p-3 rounded-full backdrop-blur-sm transition-all hover:scale-110"
        style={{
          backgroundColor: `${colors.bgSecondary}80`,
          color: colors.text,
          border: `1px solid ${colors.border}`
        }}
      >
        <ArrowLeft size={20} />
      </motion.button>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12"
        >
          {/* Image Gallery */}
          <div className="space-y-6">
            {/* Main Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
            >
              <img
                src={phone.phoneImages[selectedImage]}
                alt={phone.phoneName}
                className="w-full h-96 object-cover"
              />
            </motion.div>

            {/* Thumbnail Images */}
            <div className="grid grid-cols-4 gap-4">
              {phone.phoneImages.map((image, index) => (
                <motion.button
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedImage(index)}
                  className={`rounded-xl overflow-hidden transition-all ${
                    selectedImage === index ? 'ring-2 ring-offset-2' : 'opacity-60 hover:opacity-100'
                  }`}
                  style={{
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.border}`,
                    ringColor: colors.primary
                  }}
                >
                  <img
                    src={image}
                    alt={`${phone.phoneName} ${index + 1}`}
                    className="w-full h-20 object-cover"
                  />
                </motion.button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-6"
          >
            {/* Condition Badge */}
            <div className="flex items-center gap-4">
              <span
                className="px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2"
                style={{
                  backgroundColor: `${getConditionColor(phone.condition)}20`,
                  color: getConditionColor(phone.condition)
                }}
              >
                <CheckCircle size={16} />
                {phone.condition} Condition
              </span>
              
              {/* Like Button */}
              <LikeButton productId={phone._id} size={24} />
            </div>

            {/* Title */}
            <h1 className="text-4xl font-black leading-tight" style={{ color: colors.text }}>
              {phone.phoneName}
            </h1>

            <p className="text-lg" style={{ color: colors.textSecondary }}>
              {phone.phoneBrand} • {phone.phoneModel}
            </p>

            {/* Pricing */}
            <div className="space-y-2">
              <div className="flex items-center gap-4">
                <div className="text-4xl font-black" style={{ color: colors.primary }}>
                  ₦{phone.phonePrice?.toLocaleString()}
                </div>
                {phone.originalPrice && phone.originalPrice > phone.phonePrice && (
                  <div className="text-xl line-through" style={{ color: colors.textSecondary }}>
                    ₦{phone.originalPrice?.toLocaleString()}
                  </div>
                )}
              </div>
              
              {phone.originalPrice && phone.originalPrice > phone.phonePrice && (
                <div
                  className="px-4 py-2 rounded-full text-sm font-bold text-white w-fit"
                  style={{ backgroundColor: colors.accent }}
                >
                  Save ₦{(phone.originalPrice - phone.phonePrice)?.toLocaleString()}!
                </div>
              )}
            </div>

            {/* Key Features */}
            <div className="grid grid-cols-2 gap-4 py-4">
              {[
                { icon: HardDrive, label: 'Storage', value: phone.storage },
                { icon: Zap, label: 'RAM', value: phone.ram },
                { icon: Palette, label: 'Color', value: phone.color },
                { icon: Battery, label: 'Battery', value: phone.batteryCapacity },
                { icon: Smartphone, label: 'Screen', value: phone.screenSize },
                { icon: Shield, label: 'Warranty', value: phone.warrantyMonths ? `${phone.warrantyMonths} months` : 'No warranty' }
              ].map((item, index) => (
                item.value && (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="flex items-center gap-3 p-3 rounded-xl"
                    style={{ backgroundColor: colors.bgSecondary }}
                  >
                    <item.icon size={20} style={{ color: colors.primary }} />
                    <div>
                      <div className="text-sm font-semibold" style={{ color: colors.text }}>
                        {item.label}
                      </div>
                      <div className="text-sm" style={{ color: colors.textSecondary }}>
                        {item.value}
                      </div>
                    </div>
                  </motion.div>
                )
              ))}
            </div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { icon: Shield, text: `${phone.warrantyMonths || 12}-Month Warranty` },
                { icon: RotateCcw, text: '30-Day Return Policy' },
                { icon: CheckCircle, text: 'Quality Certified' },
                { icon: Truck, text: 'Free Delivery' }
              ].map((badge, index) => (
                <div
                  key={badge.text}
                  className="flex items-center gap-2 p-3 rounded-xl"
                  style={{ backgroundColor: colors.bgSecondary }}
                >
                  <badge.icon size={16} style={{ color: colors.primary }} />
                  <span className="text-sm font-medium" style={{ color: colors.text }}>
                    {badge.text}
                  </span>
                </div>
              ))}
            </motion.div>

            {/* Stock Status */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className={`p-4 rounded-xl ${
                phone.inStock ? 'bg-green-500/20 border-green-500/30' : 'bg-red-500/20 border-red-500/30'
              } border`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${phone.inStock ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
                <span className={`font-semibold ${phone.inStock ? 'text-green-500' : 'text-red-500'}`}>
                  {phone.inStock 
                    ? `In Stock (${phone.quantityAvailable} available)` 
                    : 'Out of Stock'
                  }
                </span>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="flex gap-4"
            >
              <button
                onClick={handleAddToCart}
                disabled={!phone.inStock || addingToCart}
                className="flex-1 py-4 px-6 rounded-xl font-bold text-white transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`
                }}
              >
                {addingToCart ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                    Adding...
                  </>
                ) : (
                  <>
                    <ShoppingCart size={20} />
                    Add to Cart
                  </>
                )}
              </button>

              <button
                className="px-6 py-4 rounded-xl font-semibold transition-all hover:scale-105 flex items-center gap-2"
                style={{
                  backgroundColor: colors.bgSecondary,
                  color: colors.primary,
                  border: `2px solid ${colors.primary}`
                }}
              >
                <Heart size={20} />
                Save
              </button>
            </motion.div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4 }}
              className="pt-6 border-t"
              style={{ borderColor: colors.border }}
            >
              <h3 className="text-xl font-bold mb-4" style={{ color: colors.text }}>
                Product Description
              </h3>
              <p className="leading-relaxed" style={{ color: colors.textSecondary }}>
                {phone.phoneDescription}
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default RefurbishedPhoneDetails;